import React, { useState, useEffect } from "react";
import { Container, Tabs, Tab, Table, Drawer, TextField, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Typography, Select, MenuItem, CircularProgress, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getAllCourtAssignments, updateBallkidJobCount, addBallkidComment, getBallkidComments, getBallkidIdByName, getActiveCrew, setActiveCrew, getAvailableBallkidsForDay, getCheckInStatus } from "../firebaseService";
import { useAuth } from "../AuthContext";
import { useTournamentConfig } from "../context/TournamentConfigContext";
import { useDefaultTournamentDay } from "../utils/useDefaultTournamentDay";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

const courts = ["Center Court", "Court 1", "Court 2", "Court 3", "Court 4"];

const CourtAllocation = () => {
  const navigate = useNavigate();
  const { daysOfWeek } = useTournamentConfig();

  const { user } = useAuth();
  const [selectedCourt, setSelectedCourt] = useState(courts[0]);
  const [selectedDay, setSelectedDay] = useDefaultTournamentDay(daysOfWeek);
  const [courtAssignments, setCourtAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
  const [activeCrews, setActiveCrews] = useState({});
  const [crewHistory, setCrewHistory] = useState([]);
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBallkid, setSelectedBallkid] = useState(null);
  const [ballkidFromTimes, setBallkidFromTimes] = useState({}); // name → "14:00" oder ""
  const [checkInStatus, setCheckInStatus] = useState({}); // ballkidId → true/false
  const [ballkidNameToId, setBallkidNameToId] = useState({}); // name → id

  useEffect(() => {
    const fetchAssignments = async () => {
      setLoading(true);
      const [assignments, availableKids, checkedIn] = await Promise.all([
        getAllCourtAssignments(selectedDay),
        getAvailableBallkidsForDay(selectedDay),
        getCheckInStatus(selectedDay),
      ]);
      setCourtAssignments(assignments);
      setCheckInStatus(checkedIn);

      // Map: name → from-time + name → id
      const fromMap = {};
      const nameIdMap = {};
      availableKids.forEach((bk) => {
        if (bk.from) fromMap[bk.name] = bk.from;
        nameIdMap[bk.name] = bk.id;
      });
      setBallkidFromTimes(fromMap);
      setBallkidNameToId(nameIdMap);

      setLoading(false);
    };

    fetchAssignments();
  }, [selectedDay]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchActiveCrew = async () => {
      const activeCrewData = await getActiveCrew(selectedDay, selectedCourt);
      if (activeCrewData) {
        setActiveCrews((prev) => ({ ...prev, [selectedCourt]: activeCrewData.activeCrew }));
        setCrewHistory(activeCrewData.history || []);
      }
    };

    fetchActiveCrew();
  }, [selectedDay, selectedCourt]);

  const handleCrewSwitch = async (crew) => {
    const switchTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const updatedHistory = [...crewHistory, { time: switchTime, crew }];

    await updateBallkidJobCount(selectedDay, selectedCourt, crew);
    await setActiveCrew(selectedDay, selectedCourt, crew, updatedHistory);

    setActiveCrews((prev) => ({ ...prev, [selectedCourt]: crew }));
    setCrewHistory(updatedHistory);
  };

  const handleRowClick = async (ballkidName) => {
    try {
      const ballkidId = await getBallkidIdByName(ballkidName);
      if (!ballkidId) {
        console.error("Keine ID für Ballkind gefunden:", ballkidName);
        return;
      }

      const ballkid = { id: ballkidId, name: ballkidName };
      setSelectedBallkid(ballkid);
      setDrawerOpen(true);

      const loadedComments = await getBallkidComments(ballkidId);
      setComments(loadedComments || []);
    } catch (error) {
      console.error("Fehler beim Laden der Ballkind-ID:", error);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    const newComment = {
      text: comment,
      timestamp: new Date().toISOString(),
      userEmail: user?.email || "Unbekannt",
    };

    await addBallkidComment(selectedBallkid.id, newComment);
    setComments([...comments, newComment]);
    setComment("");
  };

  return (
    <Box>
        <Typography variant="h5" sx={{ textAlign: "center", mb: 2 }}>
          Platzeinteilung
        </Typography>
        <Typography variant="h6">
          Aktuelle Uhrzeit: {currentTime}
        </Typography>

        <Box display="flex" alignItems="center" gap={2} mt={2} mb={2}>
          <Select
            value={selectedDay}
            onChange={(e) => setSelectedDay(e.target.value)}
            sx={{
              color: 'white',
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: 'white',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'white',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'white',
              },
              '.MuiSvgIcon-root': {
                color: 'white',
              },
            }}
          >
            {daysOfWeek.map((day) => (
              <MenuItem key={day} value={day}>{day}</MenuItem>
            ))}
          </Select>

          <Button variant="outlined" color="info" onClick={() => navigate("../court_crew_times")}>
            Crew-Zeiten
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => navigate("../einteilung")}>
            Einteilung Edit
          </Button>
          <Button variant="outlined" onClick={() => navigate("../all_assignments")}>
            Alle Einteilungen
          </Button>
        </Box>

        <Tabs
          value={selectedCourt}
          onChange={(e, newValue) => setSelectedCourt(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              color: 'white',
            },
            '& .Mui-selected': {
              color: 'white',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'white',
            },
          }}
        >
          {courts.map((court) => (
            <Tab key={court} label={court} value={court} />
          ))}
        </Tabs>

        <div>
          {loading ? (
            <CircularProgress style={{ marginTop: 20 }} />
          ) : (
            ["crewA", "crewB", "crewC", "crewD"].map((crewKey) => (
              courtAssignments[`${selectedDay}_${selectedCourt}_${crewKey}`] ? (
                <div key={crewKey}>
                  <Typography variant="h5" style={{ marginTop: 10, backgroundColor: activeCrews[selectedCourt] === crewKey ? "rgba(46, 125, 50, 0.3)" : "transparent", border: activeCrews[selectedCourt] === crewKey ? "1px solid rgba(46, 125, 50, 0.6)" : "none", padding: "5px", borderRadius: "5px" }}>
                    Crew {crewKey.replace("crew", "")} {activeCrews[selectedCourt] === crewKey ? "(Aktiv)" : ""}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Pos</TableCell>
                          <TableCell>Name</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(courtAssignments[`${selectedDay}_${selectedCourt}_${crewKey}`] || []).map((name, index) => {
                          const fromTime = ballkidFromTimes[name];
                          const bkId = ballkidNameToId[name];
                          const isCheckedIn = bkId && checkInStatus[bkId];
                          return (
                          <TableRow key={index} onClick={() => handleRowClick(name)} style={{ cursor: "pointer" }}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {isCheckedIn
                                ? <CheckCircleIcon sx={{ fontSize: 18, color: "#4caf50" }} />
                                : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.3)" }} />
                              }
                              {name || "Unbekannt"}
                              {fromTime && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: "warning.main" }}>
                                  (ab {fromTime})
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button variant="contained" color="primary" style={{ marginTop: 10 }} onClick={() => handleCrewSwitch(crewKey)}>
                    Crew {crewKey.replace("crew", "")} einwechseln
                  </Button>
                </div>
              ) : null
            ))
          )}

          <Typography variant="h6" sx={{ mt: 2 }}>
            Crew-Wechselverlauf
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {crewHistory.map((entry, index) => (
                    <TableCell key={index} align="center">{entry.time}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {crewHistory.map((entry, index) => (
                    <TableCell key={index} align="center">{entry.crew}</TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Container style={{ width: 400, padding: 20 }}>
            <h2>Kommentare zu {selectedBallkid?.name}</h2>
            <TextField
              label="Neuer Kommentar"
              multiline
              rows={3}
              fullWidth
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              variant="outlined"
              style={{ marginBottom: 10 }}
            />
            <Button variant="contained" color="primary" onClick={handleAddComment}>
              Kommentar hinzufügen
            </Button>
            <div style={{ marginTop: 20 }}>
              {comments.map((c, index) => (
                <Paper key={index} style={{ padding: 10, marginBottom: 5 }}>
                  <Typography variant="body2">{c.text}</Typography>
                  <Typography variant="caption">
                    {new Date(c.timestamp).toLocaleString()} - {c.userEmail}
                  </Typography>
                </Paper>
              ))}
            </div>
          </Container>
        </Drawer>
    </Box>
  );
};

export default CourtAllocation;
